import { ApiError } from "./api";

/**
 * Turns whatever went wrong into something a person can act on.
 *
 * One rule, learned the expensive way in the claim portal: **never truncate the
 * original message.** A `raw.split("\n")[0]` there once discarded the half of a
 * revert reason that said what to do about it. Everything here maps the cases
 * it recognises and passes the rest through intact.
 */

export type DescribedError = { code: string; message: string };

/**
 * Wallet rejections, by every name the ecosystem has for them.
 *
 * MetaMask, Rabby, Coinbase and WalletConnect all report a user cancelling
 * differently, and getting this wrong is worse than it looks: an unrecognised
 * rejection renders as a scary failure for something the user did on purpose.
 */
const REJECTION_PATTERNS = [
  "user rejected",
  "user denied",
  "user cancelled",
  "user canceled",
  "rejected the request",
  "request rejected",
  "ethers-user-denied",
  "actionrejected",
];

export function describeWalletError(error: unknown): DescribedError {
  if (error instanceof ApiError) {
    return { code: error.code, message: error.message };
  }

  const raw = messageOf(error);
  const haystack = raw.toLowerCase();

  if (REJECTION_PATTERNS.some((pattern) => haystack.includes(pattern))) {
    return {
      code: "WALLET_REJECTED",
      message: "Transaksi dibatalkan. Tidak ada ASQ yang berpindah.",
    };
  }

  if (haystack.includes("insufficient funds")) {
    return {
      code: "INSUFFICIENT_GAS",
      message:
        "Saldo native token tidak cukup untuk gas. Isi BNB testnet lalu coba lagi. " +
        `Detail: ${raw}`,
    };
  }

  if (haystack.includes("modulenotactive")) {
    return {
      code: "UTILITY_NOT_ACTIVE",
      message:
        "Modul utility ini tidak lagi aktif di registry, jadi kontrak menolak " +
        "pembayaran baru. Tidak ada ASQ yang berpindah.",
    };
  }

  if (haystack.includes("referencealreadyconsumed")) {
    return {
      code: "REFERENCE_SPENT",
      message:
        "Reference pembayaran ini sudah pernah dipakai. Mulai pembayaran baru " +
        "untuk mendapatkan reference yang segar.",
    };
  }

  if (haystack.includes("erc20insufficientallowance") || haystack.includes("allowance")) {
    return {
      code: "ALLOWANCE_TOO_LOW",
      message: `Approval ASQ belum mencukupi untuk pembayaran ini. Detail: ${raw}`,
    };
  }

  if (haystack.includes("erc20insufficientbalance")) {
    return {
      code: "INSUFFICIENT_BALANCE",
      message: `Saldo ASQ tidak mencukupi. Detail: ${raw}`,
    };
  }

  if (haystack.includes("chain mismatch") || haystack.includes("does not match the target chain")) {
    return {
      code: "WRONG_NETWORK",
      message: `Wallet berada di jaringan yang berbeda. Detail: ${raw}`,
    };
  }

  // Unrecognised. The whole message goes through — it is the only thing left
  // that might name the real problem.
  return { code: "UNKNOWN", message: raw };
}

function messageOf(error: unknown): string {
  if (typeof error === "string") return error.trim();
  if (error instanceof Error) {
    // viem stacks a short message onto a details field; both matter.
    const details = (error as { shortMessage?: string; details?: string });
    const parts = [details.shortMessage, error.message, details.details]
      .filter((part): part is string => Boolean(part))
      .map((part) => part.trim());

    /*
      De-duplicated, because viem's shortMessage is usually a prefix of message
      and printing both reads as though the error happened twice.

      A part is dropped when some *other* part already contains it — checked in
      both directions, not just against earlier ones. shortMessage comes first
      and is the shorter of the two, so a backwards-only comparison keeps
      exactly the copy it was meant to remove.
    */
    const unique = parts.filter((part, index) =>
      !parts.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          other.includes(part) &&
          // Length breaks the tie for a genuine duplicate: keep the first.
          (other.length > part.length || otherIndex < index),
      ),
    );
    return unique.join(" — ") || "Terjadi kesalahan yang tidak dikenali.";
  }
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return "Terjadi kesalahan yang tidak dikenali.";
}
